import { isNil } from "lodash-es";
import { QuillToolbarConfig } from "ngx-quill";

export { appendUrlPath } from "../environments/get-global-config";

/**
 * Whether two date/duration-like objects are equal, including possibly null
 * values.
 * @param left - The "left side" item to compare in the equality check.
 * @param right - The "right side" item to compare in the equality check.
 */
export function areLuxonModelsEqual<T extends { equals(other: T): boolean }>(
  left: T | null,
  right: T | null,
): boolean {
  return left === right || (!!left && !!right && left.equals(right));
}

/**
 * Throws an error for an unhandled switch case.
 * @param valueType - The name of the value type to include in the error message.
 * @param value - The value that violated the expectation.
 */
export function throwUnhandledCaseError(
  valueType: string,
  value: never,
): never {
  throw new Error(`Unhandled switch case for ${valueType}: ${String(value)}`);
}

/**
 * Checks whether the value exists, i.e. is not `null` or `undefined`. this is
 * **not** a truthy check, so `isExistent(0) === true`, for instance.
 * @param value The value to check.
 */
export function isExistent<T>(value: T): value is NonNullable<T> {
  return !isNil(value);
}

/**
 * Creates a function that checks its argument against the given class
 * constructor to see if the argument is an instance of the class.
 * @param classConstructor The constructor of the class to check the value against.
 */
export const isInstanceOf =
  <T>(classConstructor: ConstructorLike<T>) =>
  (value: unknown): value is T =>
    value instanceof classConstructor;

/**
 * Creates a function which determines whether its argument **does not** exactly
 * equal the provided value. Useful for use in `filter` functions.
 * @param notValue The value the function will check its values against.
 */
export function isNot<Value, ExcludedValue extends Value>(
  notValue: ExcludedValue,
): (value: Value) => value is Exclude<Value, ExcludedValue> {
  return (value: Value): value is Exclude<Value, ExcludedValue> =>
    value !== notValue;
}

/**
 * Creates a function that checks its argument value against the given array
 * to see if the value exists in (and thus is of a type from) the array.
 * @param array The array or tuple to check the value against.
 */
export const isOneOfCreator =
  <T>(array: readonly T[]) =>
  (value: unknown): value is T => {
    // It should always be safe to pass any value to `includes` regardless
    // of the types of the array or the value and have it return whether
    // the value is included.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
    return array.includes(value as any);
  };

/**
 * Gets the enum member type for the given value or throws if none is found.
 * @param enumType The enum object to get the members from.
 * @param value The value to check for in the enum.
 */
export function getEnumMember<T>(
  enumType: T,
  value: number | string,
): T[keyof T];
/**
 * Gets the enum member type for the given value. If the value is not found,
 * return the provided default member from the enum.
 * @param enumType The enum object to get the members from.
 * @param value The value to check for in the enum.
 * @param defaultMember The value to return if the provided value doesn't exist.
 */
export function getEnumMember<T>(
  enumType: T,
  value: number | string | null | undefined,
  defaultMember: T[keyof T],
): T[keyof T];
/**
 * Gets the enum member type for the given value. If the value is not found,
 * return null.
 * @param enumType The enum object to get the members from.
 * @param value The value to check for in the enum.
 * @param defaultMember The value to return if the provided value doesn't exist.
 */
export function getEnumMember<T extends Record<string, unknown>>(
  enumType: T,
  value: number | string | null | undefined,
  defaultMember: null,
): T[keyof T] | null;
export function getEnumMember<T extends Record<string, unknown>>(
  enumType: T,
  value: number | string | null | undefined,
  defaultMember?: T[keyof T] | null,
): T[keyof T] | null {
  if (isExistent(value) && isEnumMember(enumType, value)) {
    return value;
  } else if (defaultMember === undefined) {
    throw new Error(
      `Missing enum member for value "${String(
        value,
      )}" and no default was provided.`,
    );
  } else {
    return defaultMember;
  }
}

export function getEnumKey<T extends Record<string, unknown>>(
  enumType: T,
  value: T[keyof T],
): string | undefined {
  for (const key in enumType) {
    if (enumType[key] === value) {
      return key;
    }
  }
  return undefined;
}

/**
 * Gets a list of allowed Quill formats (e.g. for pasted in text) from a toolbar
 * configuration.
 *
 * This allows for limiting the allowed formats to only those supported by the
 * editor itself.
 *
 * @param toolbar - The Quill toolbar configuration to pull the allowed formats
 * from.
 */
export function getQuillFormatsFromToolbar(
  toolbar: QuillToolbarConfig,
): string[] {
  return toolbar
    .flat()
    .flatMap((option) =>
      typeof option === "string" ? option : Object.keys(option),
    )
    .filter((format) => format !== "clean");
}

function isEnumMember<T extends Record<string, unknown>>(
  enumType: T,
  value: unknown,
): value is T[keyof T] {
  return Object.values(enumType).includes(value);
}

export function hasProperty<T extends object, P extends string>(
  value: T,
  property: P,
): value is T & Readonly<Record<P, unknown>> {
  return property in value;
}

/**
 * Joins one or more path segments together, resolving any relative parent
 * segments (`..`) and removes empty segments.
 */
export function joinPath(first: string, ...rest: string[]): string {
  // Keep leading slash in first segment if present to represent the root path.
  // Remove it from everything else.
  const path = [first, ...rest.map((segment) => segment.replace(/^\/+/, ""))]
    // Remove trailing slashes from all segments so there aren't extra slashes
    // when they're all joined together again.
    .map((segment) => segment.replace(/\/+$/, ""))
    // Remove any empty segments since it's likely just a typo or trailing slash.
    .filter((segment) => segment)
    .join("/");
  // Use URL with a throw-away "base" URL to resolve any `..` segments. Also
  // remove any trailing slashes that might result, for instance:
  // `/foo/bar/..` -> `/foo/` -> `/foo`
  return new URL(path, "http://example.com").pathname.replace(/\/+$/, "");
}

export enum SortOrder {
  Ascending = "asc",
  Descending = "desc",
}
